// src/services/api/user.js
import { apiRequest } from "@/services/api/apiClient.js";
import {getCorp, updateCorp} from "@/services/api/corporation.js";
import {getUser} from "@/services/api/auth.js";

/**
 * Catalogo consultado no /appmenu.
 *
 * Vem do /config para que trocar de catalogo seja variavel de ambiente, nao
 * deploy de codigo -- e a troca importa: enquanto o catalogo proprio do
 * accounts nao existir, o valor precisa continuar sendo o do admin, senao a
 * arvore volta vazia e o menu some para quem nao for de grupo admin.
 */
export function catalogoDeMenu() {
    return window.appConfig?.APPMENU_CATALOG || 'hLiberty.Admin';
}


// O endpoint e literal de proposito: este servico e compartilhado, e importar
// a entity de dentro de um modulo inverteria a dependencia.
const ENDPOINT = "workgroup";

export async function getPermissionsManagement({ page = 1, limit = 10, ...filters }) {
    const corp = await getCorp();
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: `/workgroup?corp_id=${corp?.id ?? ''}`,
        params: { page, limit, ...filters },
    });
}

/**
 * Os grupos da empresa da sessao que a pessoa logada pode escolher para
 * adicionar um usuario -- todos, se ela administra a corporacao inteira, ou
 * so os que gerencia, se e gestora de grupo especifico.
 *
 * Endpoint dedicado (nao `getPermissionsManagement`): aquele exige a
 * permissao de rota de `isAuthorized()`, o que deixaria uma gestora pura sem
 * nem listar os proprios grupos.
 */
export async function getGruposQueGerencio() {
    const corp = await getCorp();
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: "/workgroup/gerenciaveis",
        params: { corp_id: corp?.id ?? '' },
    });
}

export async function getMenu({ page = 1, limit = 10, corpId = '', idWorkgroup = '', ...filters }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: `/appmenu/${catalogoDeMenu()}?workgroupId=${idWorkgroup}&corpId=${corpId}`,
        params: { },
    });
}

/**
 * Busca a arvore de permissoes do usuario no servidor e grava no localStorage.
 *
 * Existe porque `getMePermissionsManagement` so *le* o cache local: no admin
 * quem preenche esse cache e o login, no fluxo do setCorp. O accounts nunca
 * passa por um login -- a sessao chega pronta do admin, em outra origem, onde
 * o localStorage esta vazio. Sem esta chamada o menu lateral nasceria vazio.
 *
 * O catalogo consultado ainda e o do admin (`APPMENU_CATALOG`). As permissoes
 * de gestao de contas vivem la, e os nos que o accounts exibe sao os mesmos
 * caminhos de titulo. Ver a pendencia "Catalogo de permissoes proprio".
 */
export async function carregarPermissoesDoServidor({ userId }) {
    const catalogo = catalogoDeMenu();

    try {
        const permissoes = await apiRequest({
            client: window.API_AUTH,
            method: "get",
            url: `/appmenu/${catalogo}?userId=${userId}`,
        });

        localStorage.setItem("permissions", JSON.stringify(permissoes));
        await carregarPermissoesDeApi({ userId });

        return permissoes;
    } catch (erro) {
        // A API devolve 404 quando o catalogo nao tem nenhum item -- e e assim
        // que um catalogo novo comeca. Antes desta guarda o erro subia ate o
        // /sso, que descartava a sessao e devolvia o usuario ao admin: apontar
        // para um catalogo inexistente nao deixava o menu vazio, trancava a
        // aplicacao inteira.
        //
        // Mas entrar nao e o bastante: a condicao de administrador vem de
        // dentro desta mesma resposta (`permissions.admin`), nao do usuario.
        // Com a arvore vazia ninguem e admin, e toda tela responde 403. Por
        // isso APPMENU_CATALOG so pode ser trocado DEPOIS de o catalogo novo
        // existir -- ate la ele aponta para um que exista. Ver o passo a passo
        // em docs/README.md.
        if (erro?.response?.status === 404) {
            localStorage.setItem("permissions", JSON.stringify({}));
            console.warn(`Catalogo "${catalogo}" ainda nao tem itens de menu.`);
            await carregarPermissoesDeApi({ userId });
            return {};
        }

        throw erro;
    }
}

/**
 * Permissao por endpoint, que e a que o backend realmente aplica.
 *
 * A arvore do /appmenu diz o que aparece no *menu*; esta lista diz o que a API
 * aceita. As duas nao coincidem: uma tela so entra na arvore depois de
 * cadastrada no catalogo, e a tela que cadastra o catalogo e justamente uma
 * delas. Sem esta segunda fonte, a interface negaria um acesso que a API
 * concederia -- e o catalogo nunca poderia ser criado pela interface.
 */
async function carregarPermissoesDeApi({ userId }) {
    try {
        const resposta = await apiRequest({
            client: window.API_AUTH,
            method: "get",
            url: `/user/${userId}/permission`,
        });

        const dados = resposta?.data ?? resposta ?? {};
        localStorage.setItem("permissionsApi", JSON.stringify(dados));

        return dados;
    } catch (erro) {
        console.warn("Nao foi possivel carregar a permissao por endpoint:", erro);
        localStorage.setItem("permissionsApi", JSON.stringify({ admin: [], allow: [] }));
        return { admin: [], allow: [] };
    }
}

function permissoesDeApi() {
    try {
        return JSON.parse(localStorage.getItem("permissionsApi") || '{"admin":[],"allow":[]}');
    } catch {
        return { admin: [], allow: [] };
    }
}

/**
 * Traduz a lista de endpoints permitidos nas acoes que as telas entendem.
 *
 * Os itens de `allow` tem a forma
 *   auth.api.hliberty.net/api/service/{id}/PUT
 * entao casamos pelo sufixo, que e o que identifica recurso e metodo,
 * ignorando o host -- que muda entre ambientes.
 */
export function acoesPeloEndpoint(base) {
    const { allow } = permissoesDeApi();

    if (!Array.isArray(allow) || !allow.length) return {};

    const permite = (sufixo) => allow.some((r) => typeof r === "string" && r.endsWith(sufixo));

    const acoes = {};
    if (permite(`/api/${base}/GET`)) acoes.list = "list";
    if (permite(`/api/${base}/POST`)) acoes.create = "create";
    if (permite(`/api/${base}/{id}/GET`)) acoes.view = "view";
    if (permite(`/api/${base}/{id}/PUT`)) acoes.edit = "edit";
    if (permite(`/api/${base}/{id}/DELETE`)) acoes.delete = "delete";

    return acoes;
}


export async function getMePermissionsManagement() {
    try {
        const data = localStorage.getItem("permissions");
        return data ? JSON.parse(data) : {};
    } catch (error) {
        console.error("Error: ", error);
        return {};
    }
}

export async function setMePermissionsManagement({id, advancedCredentials = null}) {
    let permissions = "";
    let user = "";
    if (advancedCredentials) {
        user = getUser();
        permissions = user.fullUser.permission;
    } else {
        permissions = await apiRequest({
            client: window.API_AUTH,
            method: "get",
            url: `/appmenu/${catalogoDeMenu()}?userId=${id}`
        });
    }
    localStorage.setItem("permissions", JSON.stringify(permissions));
}

/**
 * Grava as permissoes do grupo.
 *
 * Deixou de passar pelo `/appmenu/{catalogo}/{grupo}`: aquele caminho faz
 * `array_intersect` contra as acoes daquele catalogo, entao marcar uma
 * permissao que nao esteja cadastrada nele era descartado em silencio. A tela
 * agora escolhe entre as 427 da plataforma.
 */
export async function addPermissions({ idWorkgroup, corpId, actions }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: `/workgroup/${idWorkgroup}/permissoes`,
        body: { corp_id: corpId, actions },
    });
}

/** Os ids de acao que o grupo ja tem -- o que a tela marca ao abrir. */
export async function permissoesDoGrupo({ id, corpId = null }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: `/workgroup/${id}/permissoes`,
        params: corpId ? { corp_id: corpId } : {},
    });
}

export async function savePermissionManagement(permissionData) {
    const isUpdate = !!permissionData.id;

    permissionData = {
        ...permissionData,
        corp_id: permissionData.corp_id,
        // Aceita id puro ou o objeto {value} do autocomplete antigo. A tela de
        // grupos passou a mandar ids; outras chamadas podem ainda mandar
        // objetos, e normalizar aqui evita quebrar quem nao migrou.
        users: (permissionData.users || []).map(u => (u && typeof u === 'object' ? u.value : u)).filter(Boolean),
        // Explicito, e nao so herdado do espalhamento acima: o backend trata
        // `managers` como declarativo -- a lista que chega E o conjunto --, e
        // `undefined` ali significa "nao mexeu". Deixar o campo sumir por
        // acidente seria indistinguivel de nao ter gestor nenhum.
        //
        // O nome do campo e ingles porque a API e: e irmao de `users` e
        // `logins` na mesma requisicao. O `gestores` daqui para dentro segue
        // portugues, como o resto do codigo.
        managers: (permissionData.gestores || []).map(u => (u && typeof u === 'object' ? u.value : u)).filter(Boolean),
    }

    let url = isUpdate
        ? `${ENDPOINT}/${permissionData.id}`
        : ENDPOINT;

    const response = await apiRequest({
        client: window.API_AUTH,
        method: isUpdate ? "put" : "post",
        url,
        body: permissionData,
    });

    // Tres estados, e a diferenca entre eles importa:
    //
    //   []    -> revoga tudo. Era o caso que nao funcionava: a chamada era
    //            pulada quando nada estava marcado, e como quem grava apaga as
    //            permissoes antes de regravar, nao chamar equivale a nao mexer.
    //            Desmarcar tudo devolvia sucesso sem revogar nada.
    //   [...] -> passa a ser a lista do grupo.
    //   null  -> NAO MEXE. E o que a tela manda quando nao conseguiu carregar
    //            as permissoes atuais; mandar [] ali revogaria tudo por causa
    //            de uma falha de leitura.
    if (permissionData.permissions !== null && permissionData.permissions !== undefined) {
        const bruto = permissionData.permissions;
        const permissionsArray = Array.isArray(bruto)
            ? bruto
            : String(bruto).split(",").map(v => v.trim()).filter(Boolean);

        await addPermissions({
            idWorkgroup: response.id ?? permissionData.id,
            corpId: permissionData.corp_id,
            actions: permissionsArray,
        });
    }
    if (isUpdate) {
        const { data } = await getPermissionsManagementById({ id: permissionData.id});
        const currentUsers = Object.keys(data.users || {});

        const markedUserIds = permissionData.users;
        const usersToAddIds = markedUserIds.filter(markedId => !currentUsers.includes(markedId));
        const usersToRemoveIds = currentUsers.filter(currentId => !markedUserIds.includes(currentId));
        for (const userId of usersToAddIds) {
            await addUsertoPermissionManagement({ idPermissionManagement: permissionData.id, idUser: userId });
        }
        for (const userId of usersToRemoveIds) {
            await removeUserFromPermissionManagement({ idPermissionManagement: permissionData.id, idUser: userId });
        }

        await updateCorp({business_name: data.corp.business_name, id: data.corp.id});
    }
    return response;
}

export const addUsertoPermissionManagement = async ({ idPermissionManagement, idUser }) => {
    try {
        return await apiRequest({
            client: window.API_AUTH,
            method: "post",
            url: `/workgroup/${idPermissionManagement}/addUser/${idUser}`,
        });
    } catch (error) {
        return Promise.reject(error);
    }
};

export const removeUserFromPermissionManagement = async ({ idPermissionManagement, idUser }) => {
    try {
        return await apiRequest({
            client: window.API_AUTH,
            method: "delete",
            url: `/workgroup/${idPermissionManagement}/remUser/${idUser}`,
        });
    } catch (error) {
        return Promise.reject(error);
    }
};

export async function getPermissionsManagementById({id}) {
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: `/workgroup/${id}`
    });
}

export async function deletePermissionsManagement({ id }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "delete",
        url: `/workgroup/${id}`
    });
}

function simplifyActions(actionsFromApi = []) {
    const allowed = actionsFromApi
        .filter(action => action.allow)
        .map(action => action.title.toLowerCase());

    const actionsObject = Object.fromEntries(allowed.map(a => [a, a]));

    return actionsObject;
}

function findPermissionByRoute(permissions, targetPath, currentPath = "") {
    for (const item of permissions) {
        const fullPath = currentPath ? `${currentPath}/${item.title}` : item.title;

        if (fullPath.toLowerCase() === targetPath.toLowerCase()) {
            return item;
        }

        if (item.menu && item.menu.length) {
            const found = findPermissionByRoute(item.menu, targetPath, fullPath);
            if (found) return found;
        }
    }

    return null;
}

/**
 * Acoes que o CATALOGO concede para esta rota -- nada do fallback por endpoint.
 *
 * Serve para a acao que nao pode aparecer por dedução. Excluir o cadastro de
 * uma pessoa na plataforma e uma delas: o fallback a liberaria para qualquer
 * um que possa chamar `DELETE /api/user/{id}`, sem que ninguem tivesse
 * concedido nada de proposito. Aqui, ou esta escrito no catalogo, ou nao tem.
 *
 * O grupo admin continua passando: ele existe justamente para cadastrar o
 * catalogo antes de o catalogo existir.
 */
export async function acoesDoCatalogo(routeKey) {
    const permissions = await getMePermissionsManagement();

    if (permissions?.admin?.[0] === "*" && permissions?.allow?.[0] === "*") {
        return { edit: "edit", view: "view", delete: "delete", create: "create", list: "list" };
    }

    const match = permissions ? findPermissionByRoute(permissions, routeKey) : null;

    return match?.menu ? simplifyActions(match.menu) : {};
}

export async function getAvailableActionsForRoute(routeKey, baseEndpoint = null) {
    const permissions = await getMePermissionsManagement();

    // Grupo admin ignora a arvore de permissoes -- e o que torna possivel
    // cadastrar o catalogo pela propria interface antes de ele existir.
    //
    // Saiu daqui um `|| routeKey.includes('QRCode')`, herdado do admin: liberar
    // acesso por a rota conter uma palavra e desvio de permissao por casamento
    // de texto, e nao sobrevive num app cujo proposito e gerir permissao. O
    // dominio QRCode nem existe aqui.
    if (permissions?.admin?.[0] === "*" && permissions?.allow?.[0] === "*") {
        return {
            edit: "edit",
            view: "view",
            delete: "delete",
            create: "create",
            list: "list",
        };
    }

    const match = permissions ? findPermissionByRoute(permissions, routeKey) : null;

    if (match?.menu) {
        return simplifyActions(match.menu);
    }

    // A rota ainda nao esta na arvore do catalogo. Isso e o estado normal de
    // uma tela recem-criada -- inclusive a propria tela de Catalogo, que
    // precisaria estar cadastrada nela mesma para funcionar. Cair para a
    // permissao que a API aplica de fato resolve o ovo e a galinha sem abrir
    // mao de conferir permissao: quem nao pode chamar o endpoint continua sem
    // ver a tela.
    return baseEndpoint ? acoesPeloEndpoint(baseEndpoint) : {};
}
